import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CloseIcon,
	FeedbackSideIcon,
	LogoutIcon,
	UserIcon,
} from '../assets/icons/Icons';
import TopNavigationBar from './TopNavigationBar';
import Logo from '../assets/images/Logo.png';
import {
	Grid,
	IconButton,
	styled,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { getSideBarStatus } from '../context/MainProvider';

const drawerWidth = 240;

const openedMixin = theme => ({
	width: drawerWidth,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	overflowX: 'hidden',
});

const closedMixin = theme => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	width: `calc(${theme.spacing(7)} + 1px)`,
	[theme.breakpoints.up('sm')]: {
		width: `calc(${theme.spacing(9)} + 1px)`,
	},
});

function CustomDrawer({
	tabName,
	listItems,
	onClickTab,
	onClose,
	navigate,
	isScreenInMobileMode = false,
	open,
	onChangeDrawerVisibility,
}) {
	const Drawer = styled(MuiDrawer, {
		shouldForwardProp: prop => prop !== 'open',
	})(({ theme }) => ({
		width: isScreenInMobileMode ? '100%' : drawerWidth,
		flexShrink: 0,
		'& .MuiDrawer-paper': {
			width: isScreenInMobileMode ? '100%' : drawerWidth,
			boxSizing: 'border-box',
			borderWidth: '0px',
			marginTop: isScreenInMobileMode ? '0px' : '50px',
			height: isScreenInMobileMode ? '100%' : '93%',
		},
		whiteSpace: 'nowrap',
		boxSizing: 'border-box',
		transition: 'width 0.3s ease',
		variants: [
			{
				props: ({ open }) => open,
				style: isScreenInMobileMode
					? { width: '100%' }
					: {
							...openedMixin(theme),
							'& .MuiDrawer-paper': openedMixin(theme),
						},
			},
			{
				props: ({ open }) => !open,
				style: {
					...closedMixin(theme),
					'& .MuiDrawer-paper': closedMixin(theme),
				},
			},
		],
	}));

	function getIconColor(selectedTabName, route) {
		if (
			selectedTabName === route ||
			selectedTabName.substring(0, 8) === route
		) {
			return '#28696D';
		} else {
			return '#7C7C7C';
		}
	}

	return (
		<Drawer open={open} variant="permanent" anchor="left" elevation={3}>
			{isScreenInMobileMode && (
				<Toolbar
					style={{
						paddingLeft: '15px',
						justifyContent: 'space-between',
					}}>
					<Grid
						container
						flexDirection={'row'}
						alignItems={'center'}
						marginTop={'20px'}>
						<Box
							component={'img'}
							src={Logo}
							sx={{
								padding: '2px',
								boxShadow: 2,
								borderRadius: '5px',
							}}
							width={180}
							height={45}
						/>
					</Grid>
					<IconButton onClick={onClose}>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			)}
			{!isScreenInMobileMode && (
				<IconButton
					sx={{
						position: 'fixed',
						top: 50,
						left: open ? 220 : 50,
						zIndex: 2000,
					}}
					onClick={onChangeDrawerVisibility}>
					{open ? <ArrowLeftIcon /> : <ArrowRightIcon />}
				</IconButton>
			)}
			<List style={{ paddingTop: '50px' }}>
				{listItems.map((text, index) => (
					<ListItem
						key={index}
						disablePadding
						style={{
							backgroundColor:
								tabName === text.route ||
								tabName.substring(0, 8) === text.route
									? '#EBFAF8'
									: 'white',
							marginLeft: open ? '8px' : '3px',
							marginTop: '10px',
							marginBottom: '10px',
							width: '95%',
							borderRadius: '6px',
						}}>
						<ListItemButton
							component={Link}
							to={text.route}
							style={open ? {} : { justifyContent: 'center' }}
							onClick={() => onClickTab(text.route)}>
							{text.route === '/admin' && (
								<UserIcon
									color={getIconColor(tabName, text.route)}
								/>
							)}
							{text.route === '/feedback' && (
								<FeedbackSideIcon
									color={getIconColor(tabName, text.route)}
								/>
							)}
							{open && (
								<Typography
									variant="sideNavFont"
									style={{
										paddingLeft: '20px',
										color: getIconColor(
											tabName,
											text.route,
										),
									}}>
									{text.name}
								</Typography>
							)}
						</ListItemButton>
					</ListItem>
				))}
			</List>
			<List style={{ position: 'absolute', bottom: 0, width: '90%' }}>
				<ListItem
					disablePadding
					style={{
						backgroundColor: 'white',
						marginLeft: open ? '15px' : '5px',
						width: '90%',
						borderRadius: '6px',
					}}>
					<ListItemButton
						component={Link}
						style={{ justifyContent: 'center' }}
						to={'/'}
						onClick={() => {
							localStorage.clear();
							navigate('/');
						}}>
						<LogoutIcon color={getIconColor(tabName, '/')} />
						{open && (
							<Typography
								variant="sideNavFont"
								style={{
									paddingLeft: '10px',
									color: getIconColor(tabName, '/'),
								}}>
								Log out
							</Typography>
						)}
					</ListItemButton>
				</ListItem>
			</List>
		</Drawer>
	);
}

export default function SuperAdminSideNavigationBar(props) {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const { isSidebarOpen, toggleSidebar } = getSideBarStatus();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [tabName, setTabName] = useState('/');
	const [openDrawerInMobile, setOpenDrawerInMobile] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const listItems = [
		{
			name: 'Users',
			route: '/admin',
		},
		{
			name: 'Care Note Feedbacks',
			route: '/feedback',
		},
	];

	useEffect(() => {
		setTabName(location.pathname);
	}, [location.pathname]);

	function openDrawerInMobileMode() {
		setOpenDrawerInMobile(!openDrawerInMobile);
	}

	const handleClick = event => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	if (isMobile) {
		return (
			<>
				<CssBaseline />
				<TopNavigationBar
					drawerWidth={0}
					onClickLogo={openDrawerInMobileMode}
					tabName={tabName}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				{openDrawerInMobile && (
					<CustomDrawer
						tabName={tabName}
						listItems={listItems}
						onClickTab={value => {
							setTabName(value);
						}}
						isScreenInMobileMode={isMobile}
						onClose={openDrawerInMobileMode}
						navigate={navigate}
						open={true}
					/>
				)}
			</>
		);
	} else {
		return (
			<Box sx={{ display: 'flex' }}>
				<CssBaseline />
				<TopNavigationBar
					filterFunction={e => props.filterFunction(e)}
					tabName={tabName}
					anchorEl={anchorEl}
					open={open}
					handleClose={handleClose}
					handleClick={handleClick}
				/>
				<CustomDrawer
					tabName={tabName}
					listItems={listItems}
					onClickTab={value => {
						setTabName(value);
					}}
					navigate={navigate}
					open={isSidebarOpen}
					onChangeDrawerVisibility={toggleSidebar}
				/>
			</Box>
		);
	}
}
